import React, { useMemo } from 'react';
import type { QuestionOption } from '@/types';
import { Card, CardContent } from '../ui/card';
import { Icon } from '../icons/Icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

type AdditionalResourcesProps = {
  options?: QuestionOption[];
};

type AdditionalResourceItem = {
  key: string;
  title: string;
  href: string | null;
};

const normalizeUrl = (raw?: string | null): string | null => {
  const value = (raw ?? '').trim();
  if (!value) {
    return null;
  }

  const hasProtocol = /^(https?:)?\/\//i.test(value);
  const candidate = hasProtocol ? value : `https://${value}`;

  try {
    const url = new URL(candidate);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
};

const openLink = (href: string | null) => {
  if (!href) {
    return;
  }
  window.open(href, '_blank', 'noopener,noreferrer');
};

export const AdditionalResources: React.FC<AdditionalResourcesProps> = ({ options }) => {
  const resources = useMemo<AdditionalResourceItem[]>(() => {
    if (!Array.isArray(options)) {
      return [];
    }

    return options.reduce<AdditionalResourceItem[]>((acc, option, index) => {
      const title = (option.front_text ?? '').trim();
      const normalizedUrl = normalizeUrl(option.back_text);

      if (!title && !normalizedUrl) {
        return acc;
      }

      const key = option.id != null ? String(option.id) : `resource-${index}`;

      acc.push({
        key,
        title: title || `Resource ${index + 1}`,
        href: normalizedUrl,
      });

      return acc;
    }, []);
  }, [options]);

  if (!resources.length) {
    return (
      <div className="w-full rounded-xl border border-dashed border-border/70 bg-white/40 p-6 text-center text-sm text-muted-foreground">
        No additional resources were provided for this question.
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex w-full flex-col gap-4">
        {resources.map((resource) => {
          const isInteractive = Boolean(resource.href);

          const handleClick = () => {
            openLink(resource.href);
          };

          const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!resource.href) {
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openLink(resource.href);
            }
          };

          const card = (
            <Card
              key={resource.key}
              maxWidth="none"
              className={`w-full rounded-md border border-border/60 bg-white/70 shadow-sm transition hover:shadow-md focus-within:ring-2 focus-within:ring-primary/40 ${
                isInteractive ? 'cursor-pointer' : ''
              }`}
              padding="compact"
              role={isInteractive ? 'button' : undefined}
              tabIndex={isInteractive ? 0 : undefined}
              onClick={isInteractive ? handleClick : undefined}
              onKeyDown={isInteractive ? handleKeyDown : undefined}
            >
              <CardContent className="w-full items-start gap-3 p-2">
                <div className="flex w-full items-center gap-3">
                  <Icon name="hyperlink" size={12} className="min-w-[0.75rem]" />
                  <div className="flex flex-col">
                    <span className="text-base text-foreground">{resource.title}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );

          if (!resource.href) {
            return card;
          }

          return (
            <Tooltip key={resource.key}>
              <TooltipTrigger asChild>{card}</TooltipTrigger>
              <TooltipContent side="top" align="start">
                {resource.href}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default AdditionalResources;
